import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import _ from "lodash";
import { useFragment } from "react-relay";

import { mergeEligibleWindowCharges } from "../../../utils/mergeEligibleWindowCharges";
import { isStaffAdminAtom, userTypeAtom } from "../atoms/eventSignUpProcess";
import { useGetPrimaryChargeNameAndPrice_event$key } from "./__generated__/useGetPrimaryChargeNameAndPrice_event.graphql";

type useGetAllPrimaryChargeSelectionOptionsProps = {
  eventRef: useGetPrimaryChargeNameAndPrice_event$key;
};

export const useGetPrimaryChargeNameAndPrice = ({
  eventRef,
}: useGetAllPrimaryChargeSelectionOptionsProps) => {
  const event = useFragment(
    useGetPrimaryChargeNameAndPriceFragmentString,
    eventRef
  );
  const isMemberUsingForm = useAtomValue(userTypeAtom) === "MEMBER";
  const isStaffAdmin = useAtomValue(isStaffAdminAtom);

  // Members see charges from the windows they're eligible for (the backend
  // strips HIDDEN charges); staff see charges across all windows, gated by
  // staffControl + their admin role.
  const charges = mergeEligibleWindowCharges(
    isMemberUsingForm
      ? event.eligibleWindowsForCharges
      : event.signUpWindows?.edges?.map((e) => e?.node)
  );

  const primaryChargeOptions = charges.flatMap(
    ({ node, memberControl, staffControl }) => {
      if (isMemberUsingForm) {
        if (memberControl !== "SELECTABLE") {
          return [];
        }
      } else {
        // Staff can pick any charge except ADMIN_ONLY, which requires an admin.
        const staffCanSelect = staffControl !== "ADMIN_ONLY" || isStaffAdmin;
        if (!staffCanSelect) {
          return [];
        }
      }

      const price = node.chargeRuleSet.chargeRules?.edges?.[0]?.node?.price;
      const currentChargeRuleRawPrice = _.isNumber(price) ? price : null;

      // A $0 charge (free/comped tier) is valid — gate on presence, not truthiness.
      if (
        node.id &&
        node.chargeRuleSet.displayName &&
        currentChargeRuleRawPrice !== null
      ) {
        return [
          {
            primaryChargeId: node.id,
            chargeName: node.chargeRuleSet.displayName,
            chargePrice: currentChargeRuleRawPrice,
          },
        ];
      }

      return [];
    }
  );

  return primaryChargeOptions;
};

const useGetPrimaryChargeNameAndPriceFragmentString = graphql`
  fragment useGetPrimaryChargeNameAndPrice_event on Event {
    signUpWindows {
      edges {
        node {
          charges {
            edges {
              memberControl
              staffControl
              node {
                id
                chargeRuleSet {
                  id
                  displayName
                  chargeRules {
                    edges {
                      node {
                        price
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
    # Member users see only the windows they are eligible for (caller-scoped).
    # Aliased so it never field-merges with other clientEligibleSignUpWindows
    # selections (e.g. the pricing section) that pass a clientId argument.
    eligibleWindowsForCharges: clientEligibleSignUpWindows {
      charges {
        edges {
          memberControl
          staffControl
          node {
            id
            chargeRuleSet {
              id
              displayName
              chargeRules {
                edges {
                  node {
                    price
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;
